import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ROLE_LABELS } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Pencil, Trash2, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { username: "", name: "", password: "", confirm_password: "", role: "kasir", financial_visibility: 100 };

export default function Users() {
  const { user, isViewOnly } = useAuth();
  const isMaster = user.role === "master";
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [showPw, setShowPw] = useState({});

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const roleOptions = isMaster ? ["master", "admin", "gm", "kasir"] : ["admin", "gm", "kasir"];

  const create = async () => {
    if (form.password !== form.confirm_password) { toast.error("Konfirmasi password tidak cocok"); return; }
    try { await api.post("/users", form); toast.success("User dibuat"); setForm(null); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const saveEdit = async () => {
    try {
      const payload = { name: editForm.name, role: editForm.role, active: editForm.active };
      if (isMaster) payload.financial_visibility = editForm.financial_visibility;
      if (editForm.password) payload.password = editForm.password;
      await api.put(`/users/${editForm.id}`, payload);
      toast.success("User diperbarui"); setEditForm(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Hapus user?")) return; try { await api.delete(`/users/${id}`); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };

  return (
    <div className="space-y-5" data-testid="users-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold font-display text-slate-900">Manajemen User</h1>
          <p className="text-sm text-slate-500">Kelola pengguna, peran & visibilitas keuangan{isMaster && " · Master dapat melihat password polos untuk pemulihan akun"}</p>
        </div>
        {!isViewOnly && <Button onClick={() => setForm({ ...EMPTY })} className="bg-orange-500 hover:bg-orange-600" data-testid="add-user-button"><Plus className="h-4 w-4 mr-1.5" /> Tambah User</Button>}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-slate-50">
            <TableHead>Username</TableHead><TableHead>Nama</TableHead><TableHead>Peran</TableHead>
            {isMaster && <TableHead>Visibilitas</TableHead>}{isMaster && <TableHead>Password</TableHead>}
            <TableHead>Status</TableHead>{!isViewOnly && <TableHead className="text-right">Aksi</TableHead>}
          </TableRow></TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id}>
                <TableCell className="font-mono text-xs">{u.username}</TableCell>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell><Badge variant="outline">{ROLE_LABELS[u.role]}</Badge></TableCell>
                {isMaster && <TableCell>{["admin", "gm"].includes(u.role) ? <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100">{u.financial_visibility}%</Badge> : <span className="text-slate-300">—</span>}</TableCell>}
                {isMaster && <TableCell><button onClick={() => setShowPw({ ...showPw, [u.id]: !showPw[u.id] })} className="flex items-center gap-1 text-xs text-slate-500 font-mono">{showPw[u.id] ? <><EyeOff className="h-3 w-3" /> <span data-testid={`user-plain-password-${u.username}`} className="text-slate-800 font-semibold">{u.password_plain || "(tidak tersedia — reset password)"}</span></> : <><Eye className="h-3 w-3" /> ••••••••</>}</button></TableCell>}
                <TableCell><Badge className={u.active ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-slate-100 text-slate-500 hover:bg-slate-100"}>{u.active ? "Aktif" : "Nonaktif"}</Badge></TableCell>
                {!isViewOnly && <TableCell className="text-right">
                  <Button size="icon" variant="ghost" onClick={() => setEditForm({ ...u, password: "" })}><Pencil className="h-4 w-4" /></Button>
                  {u.role !== "master" && <Button size="icon" variant="ghost" className="text-rose-500" onClick={() => del(u.id)}><Trash2 className="h-4 w-4" /></Button>}
                </TableCell>}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Create */}
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>Tambah User</DialogTitle></DialogHeader>
          {form && <div className="space-y-3">
            <div className="space-y-1"><Label>Username</Label><Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="user-username-input" /></div>
            <div className="space-y-1"><Label>Nama Lengkap</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" /></div>
            <div className="space-y-1"><Label>Peran</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>{roleOptions.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
              </Select></div>
            {isMaster && ["admin", "gm"].includes(form.role) && (
              <div className="space-y-1"><Label>Visibilitas Keuangan</Label>
                <Select value={String(form.financial_visibility)} onValueChange={(v) => setForm({ ...form, financial_visibility: Number(v) })}>
                  <SelectTrigger data-testid="user-visibility-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="30">30%</SelectItem><SelectItem value="50">50%</SelectItem><SelectItem value="100">100%</SelectItem></SelectContent>
                </Select></div>
            )}
            <div className="space-y-1"><Label>Password</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" /></div>
            <div className="space-y-1"><Label>Konfirmasi Password</Label><Input type="password" value={form.confirm_password} onChange={(e) => setForm({ ...form, confirm_password: e.target.value })} data-testid="user-confirm-password-input" /></div>
          </div>}
          <DialogFooter><Button onClick={create} className="bg-orange-500 hover:bg-orange-600" data-testid="save-user-button">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit */}
      <Dialog open={!!editForm} onOpenChange={(o) => !o && setEditForm(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>Edit User</DialogTitle></DialogHeader>
          {editForm && <div className="space-y-3">
            <div className="space-y-1"><Label>Nama</Label><Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /></div>
            <div className="space-y-1"><Label>Peran</Label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })} disabled={editForm.role === "master"}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{roleOptions.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
              </Select></div>
            {isMaster && ["admin", "gm"].includes(editForm.role) && (
              <div className="space-y-1"><Label className="flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5 text-orange-500" /> Visibilitas Keuangan</Label>
                <Select value={String(editForm.financial_visibility)} onValueChange={(v) => setEditForm({ ...editForm, financial_visibility: Number(v) })}>
                  <SelectTrigger data-testid="edit-visibility-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="30">30%</SelectItem><SelectItem value="50">50%</SelectItem><SelectItem value="100">100%</SelectItem></SelectContent>
                </Select></div>
            )}
            <div className="space-y-1"><Label>Reset Password (opsional)</Label><Input type="password" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} placeholder="Kosongkan jika tidak diubah" /></div>
            <div className="flex items-center gap-2"><Switch checked={editForm.active} onCheckedChange={(v) => setEditForm({ ...editForm, active: v })} /> <Label>Akun Aktif</Label></div>
          </div>}
          <DialogFooter><Button onClick={saveEdit} className="bg-orange-500 hover:bg-orange-600" data-testid="update-user-button">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
